import React, { useCallback, useEffect, useState } from "react";

import ArrowRight from "images/icons/arrow-right.inline.svg";
import ParentPackImg from "images/parent-pack.png";
import TravelSmartImg from "images/travelsmart-checklist.png";

import { useTheme } from "../../context/ThemeContext";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { shortcodePreProcessor } from "../../utils/formatContent";
import { gvi_open_days_config } from "../../utils/sharedDatasets";
import SchoolsCard from "../cards/schoolsCard";
import { FullwidthBannerBGImage } from "../shared/fullwidthBannerBGImage";
import DownloadApps from "../text-page/downloadApps";
import TextAccordionList from "../text-page/textAccordionList";
import TextAccordionNestedList from "../text-page/textAccordionNestedList";
import TextArticles from "../text-page/textArticles";
import TextCardList from "../text-page/textCardList";
import TextContentFullwidthList from "../text-page/textContentFullwidthList";
import TextExtraContent from "../text-page/textExtraContent";
import TextExtraCTA from "../text-page/textExtraCTA";
import TextIntro from "../text-page/textIntro";
import TextPageVideoGallery from "../text-page/textPageVideoGallery";
import TextTabs from "../text-page/textTabs";
import TextWideImage from "../text-page/textWideImage";

const TextPageComponent = ({
  page,
  genericDetails,
  schoolTrips,
  showSchools,
  schoolTitle,
  theme
}) => {
  const { currentSiteMeta } = theme;
  const { acfLandingPage } = page;

  const breakpoint = useBreakpoint();

  const termsConditionsContent =
    genericDetails?.globalProgramConfig?.acfTermsAndConditions
      ?.gblTermsAndConditions ??
    page?.content ??
    "";

  const groupTermsConditionsContent = page?.content ?? "";

  const programFees =
    genericDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? [];

  const shortcodeConfig = {
    programFees,
    currentSiteMeta
  };

  let pageContent;
  switch (page?.slug) {
    case "terms-and-conditions":
      pageContent = termsConditionsContent;
      break;
    case "groups-terms-and-conditions":
      pageContent = groupTermsConditionsContent;
      break;
    default:
      pageContent = page?.content;
  }

  const introText = {
    title: page?.title,
    description: shortcodePreProcessor({
      ...shortcodeConfig,
      description: pageContent
    }),
    lastUpdated: page?.modified ?? "",
    showLastUpdated: !!acfLandingPage?.genTextShowLastUpdated,
    lastUpdatedOverwrite: acfLandingPage?.genTextShowLastUpdatedOverwrite
  };

  const isClimateActionPage = page?.slug?.includes("climate-action");
  const isSDGGoalPage = page?.slug?.includes("goal-");
  const isBursaries = page?.slug?.includes("bursaries");
  const isBecomeAmbassador = page?.slug?.includes("become-ambassador");
  const isOpenDay = page?.slug?.includes("gvi-open-day");
  const isTermsAndConditions = page?.slug?.includes("terms-and-conditions");

  const scrollContainerClass =
    isTermsAndConditions || !!acfLandingPage?.genTextLongFormat ? "h-108" : "";

  const contentTabs =
    acfLandingPage?.genContentTabs
      ?.filter((tab) => tab)
      .map((tab) => ({
        title: tab?.genTabTitle ?? "",
        desc: `<div class="tab-content_html">${shortcodePreProcessor({
          ...shortcodeConfig,
          description: tab?.tabContentDesc ?? ""
        })}</div>`
      })) ?? [];

  const parentPackData = {
    title: acfLandingPage?.genPpackTitle,
    desc: shortcodePreProcessor({
      ...shortcodeConfig,
      description: acfLandingPage?.genPpackSummary
    }),
    url: acfLandingPage?.genPpackCtaLink ?? false,
    linkTitle: acfLandingPage?.genPpackCtaTitle,
    imgSrc: acfLandingPage?.genPpackImage?.sourceUrl ?? ParentPackImg
  };

  const travelSmartData = {
    title: acfLandingPage?.genChkTitle,
    desc: shortcodePreProcessor({
      ...shortcodeConfig,
      description: acfLandingPage?.genChkSummary
    }),
    url: acfLandingPage?.genChkCtaLink ?? false,
    linkTitle: acfLandingPage?.genChkCtaTitle,
    imgSrc: acfLandingPage?.genChkImage?.sourceUrl ?? TravelSmartImg
  };

  const downloadableItemsConfig = {
    parentPackData,
    travelSmartData,
    innerContainerClass: "w-11/12 md:w-9/12 py-20 mx-auto",
    outerContainerClass: isSDGGoalPage
      ? "bg-yellow-standard/30 planet:bg-gray-standard"
      : "bg-white planet:bg-gray-standard"
  };

  const fullwidthContentList = {
    sectionTitle: acfLandingPage?.genCfbSectionTitle ?? "",
    list: acfLandingPage?.genCfbList?.map((item) => ({
      title: item?.genCfbListTitle,
      ctaTitle: item?.genCfbListCtaTitle,
      ctaLink: item?.genCfbListCtaTarget,
      imgSrc: item?.genCfbListImage?.sourceUrl,
      desc: shortcodePreProcessor({
        ...shortcodeConfig,
        description: item?.genCfbListContent ?? ""
      })
    }))
  };

  const accordionContentList =
    acfLandingPage?.genAccordionList?.map((accordion) => ({
      title: accordion?.genArcTitle,
      desc: shortcodePreProcessor({
        ...shortcodeConfig,
        description: accordion?.genArcContent
      })
    })) ?? [];

  const accordionNestedContentList =
    acfLandingPage?.genAccordionNestedList?.map((accordion) => ({
      sectionTitle: accordion?.genArcNestMainTitle,
      sectionDesc: shortcodePreProcessor({
        ...shortcodeConfig,
        description: accordion?.genArcNestMainDesc
      }),
      accordionList: accordion?.genArcNestMainList?.map((accordionItem) => ({
        title: accordionItem?.genArcNestTitle,
        desc: shortcodePreProcessor({
          ...shortcodeConfig,
          description: accordionItem?.genArcNestContent
        })
      }))
    })) ?? [];

  //need to understand how this is being used
  const hasIllustration = false;

  const caseStudiesCTATitle = acfLandingPage?.genCsbListMoreCtaTitle;
  const caseStudiesCTALink = acfLandingPage?.genCsbListMoreCtaUrl;
  const caseStudies =
    acfLandingPage?.genCsbList
      ?.map((caseStudy, idx) => ({
        id: idx,
        img: null,
        sourceUrl: caseStudy?.genCsbListImage?.sourceUrl,
        title: caseStudy?.genCsbListTitle,
        desc: shortcodePreProcessor({
          ...shortcodeConfig,
          description: caseStudy?.genCsbListContent
        }),
        uri: caseStudy?.genCsbListCtaTarget,
        ctaTitle: caseStudy?.genCsbListCtaTitle ?? "Read more"
      }))
      ?.filter((item) => item?.title) ?? [];

  const caseStudyConfig = {
    articleList: caseStudies,
    viewMoreLink: caseStudiesCTALink,
    viewMoreLabel: caseStudiesCTATitle
  };

  const contentGridBlockSectionTitle =
    acfLandingPage?.genContentGridBlockTitle ?? "";

  const contentGridBlocks =
    acfLandingPage?.genContentGridBlock?.map((block) => ({
      title: block?.genCgbTitle,
      subtitle: page?.slug === "schools" ? "" : block?.genCgbSubtitle ?? "",
      description: shortcodePreProcessor({
        ...shortcodeConfig,
        description: block?.genCgbSummary ?? ""
      }),
      url: block?.genCgbCtaLink ?? false,
      imgUrl: block?.genCgbImage?.sourceUrl ?? "",
      imgMobileUrl: block?.genCgbImageMobile?.sourceUrl ?? "",
      linkTitle: block?.genCgbCtaTitle ?? "Read more",
      externalLink: block?.genCgbExternalLink === true
    })) ?? [];

  const featuredBanner = {
    sourceUrl:
      breakpoint === "xs"
        ? acfLandingPage?.genFwbImageMobile?.sourceUrl ||
          acfLandingPage?.genFwbImage?.sourceUrl
        : acfLandingPage?.genFwbImage?.sourceUrl,
    btnUrl: acfLandingPage?.genFwbImageCtaLink ?? false,
    btnLabel: acfLandingPage?.genFwbImageCtaTitle ?? "Find out more"
  };

  const brandMessageBanner = {
    genBrandMessageTitle: acfLandingPage?.genBrandMessageTitle ?? "",
    genBrandMessageSubtitle: acfLandingPage?.genBrandMessageSubtitle ?? "",
    genBrandMessageImage:
      breakpoint === "xs"
        ? acfLandingPage?.genBrandMessageImageMobile ||
          acfLandingPage?.genBrandMessageImage
        : acfLandingPage?.genBrandMessageImage ?? {}
  };

  const openDayGallery = {
    title:
      genericDetails?.globalProgramConfig?.acfGenericGlobalConfig
        ?.genOpendayFeaturedVideosTitle ??
      gvi_open_days_config?.heading ??
      "",
    subtitle:
      genericDetails?.globalProgramConfig?.acfGenericGlobalConfig
        ?.genOpendayFeaturedVideosSubtitle ??
      gvi_open_days_config?.sub_heading ??
      "",
    videos:
      genericDetails?.globalProgramConfig?.acfGenericGlobalConfig
        ?.genOpendayFeaturedVideos ?? []
  };

  const extraContent = {
    title: acfLandingPage?.genTextExtraContentBlockSectionTitle,
    description: shortcodePreProcessor({
      ...shortcodeConfig,
      description: acfLandingPage?.genTextExtraContentBlockContent
    }),
    showIllustration: !!acfLandingPage?.genTextExtraContentBlockIllustration,
    longFormat: !!acfLandingPage?.genTextExtraContentBlockLongFormat
  };

  const extraCTAButton = {
    btnLabel: acfLandingPage?.genTextExtraCtaTitle,
    btnTarget: acfLandingPage?.genTextExtraSectionCtaUrl
  };

  const [visibleCnt, setVisibleCnt] = useState(6);
  const [pagination, setPagination] = useState(true);

  useEffect(() => {
    setVisibleCnt(6);
  }, [breakpoint]);

  useEffect(() => {
    if (visibleCnt > schoolTrips?.length) setPagination(false);
  }, [schoolTrips?.length, visibleCnt]);

  const handlePagination = useCallback(() => {
    setVisibleCnt((prevState) => prevState + 6);
  }, []);

  const [isOpenTag, setOpenTag] = useState("");

  useEffect(() => {
    const anchorTag = window.location.hash.slice(1);
    if (anchorTag) {
      const anchorContainer = document.querySelector(`#${anchorTag}`);
      if (anchorContainer) {
        anchorContainer.scrollIntoView({ behavior: "smooth", block: "center" });
        setOpenTag(anchorTag);
      }
    }
  }, []);

  return (
    <>
      {introText && (
        <TextIntro
          content={introText}
          programFees={programFees}
          isIllustration={hasIllustration}
          scrollContainerClass={scrollContainerClass}
          currentSiteMeta={currentSiteMeta}
        />
      )}

      {contentTabs?.length > 0 && <TextTabs tabsDetails={contentTabs} />}

      {((parentPackData?.desc && parentPackData?.title) ||
        (travelSmartData?.title && travelSmartData?.desc)) && (
        <DownloadApps {...downloadableItemsConfig} />
      )}

      {fullwidthContentList?.list && fullwidthContentList?.list?.length > 0 && (
        <TextContentFullwidthList contentList={fullwidthContentList} />
      )}

      {(extraContent?.title || extraContent?.description) &&
        isClimateActionPage && (
          <TextExtraContent isIllustration={false} content={extraContent} />
        )}

      {contentGridBlocks && contentGridBlocks?.length > 0 && (
        <div className="bg-generic-bg planet:bg-gray-standard py-10 sm:py-20">
          {contentGridBlockSectionTitle && (
            <div
              className="w-11/12 md:w-9/12 flex mx-auto h2 sm:pb-2"
              dangerouslySetInnerHTML={{
                __html: contentGridBlockSectionTitle
              }}
            />
          )}
          <TextCardList
            articleList={contentGridBlocks}
            innerContainerClass={
              "relative w-11/12 md:w-9/12 mx-auto py-10 bg-inherit"
            }
            cardBoxCoverClass={`sm:gvi-program-card-box ${isClimateActionPage ? "bg-white planet:gvi-dark-gray-bg h-full rounded-b" : "bg-transparent"}`}
            headerTitleContainerClass={`pb-4 flex flex-row items-center ${isClimateActionPage ? "planet:text-white" : "border-b border-gray-lighter text-black-lighter planet:text-gray-generic"} justify-between`}
          />
        </div>
      )}

      {showSchools && schoolTrips && schoolTrips?.length > 0 && (
        <div
          className="bg-gray-lighten planet:bg-gray-darkest"
          id={"view-schools"}
        >
          <div className="relative w-full mx-auto sm:w-4/5 pt-16 pb-8 px-4 sm:px-0">
            <h1
              className="w-4/5 sm:w-full text-[26px] sm:text-[38px] font-normal planet:font-light font-alice planet:font-body planet:text-white pb-8"
              dangerouslySetInnerHTML={{ __html: schoolTitle }}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-8 max-h-[32rem] sm:max-h-full gvi-scrollbar-thin tab-content_html masked-overview masked-overview-down pr-2 sm:pr-0 ">
              {schoolTrips &&
                schoolTrips?.length > 0 &&
                schoolTrips?.slice(0, visibleCnt)?.map((schoolTrip, idx) => {
                  const title = schoolTrip?.title;
                  const slug = schoolTrip?.slug;
                  const subTitle = schoolTrip?.acfSchoolTrips?.stpSubtitle;
                  const bgImage = schoolTrip?.featuredImage?.node?.sourceUrl;
                  const location = schoolTrip?.acfSchoolTrips?.stpLocation;
                  const tripDate = schoolTrip?.acfSchoolTrips?.stpTripDate;

                  return (
                    <div className="flex flex-col" key={idx}>
                      <SchoolsCard
                        slug={slug}
                        schoolTitle={title}
                        subTitle={subTitle}
                        bgImage={bgImage}
                        location={location}
                        tripDate={tripDate}
                        isRounded={true}
                        isRoundedTop={false}
                        key={`school-trip-${idx}`}
                      />
                    </div>
                  );
                })}

              {schoolTrips?.length > visibleCnt && (
                <div
                  className={
                    "col-span-full w-full md:px-0 px-10 pt-10 flex justify-center items-center"
                  }
                >
                  <button
                    onClick={() => handlePagination()}
                    className="group flex flex-row items-center justify-center px-2 py-2 w-48
                bg-white planet:bg-transparent hover:bg-black-lighter planet:hover:bg-white
                border border-black-lighter hover:border-white planet:border-white planet:hover:border-white rounded-full planet:rounded-sm
                text-sm text-black-lighter hover:text-white planet:text-white planet:hover:text-gray-darker"
                  >
                    <p className="text-base font-bold pr-4">View more</p>
                    <ArrowRight className="w-3 h-3 stroke-black-lighter group-hover:stroke-white planet:stroke-white planet:group-hover:stroke-gray-darker rotate-90" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {accordionNestedContentList?.length > 0 && isBecomeAmbassador ? (
        <TextAccordionNestedList
          containerClass="w-full flex bg-white planet:bg-gray-darker py-10 sm:pt-20 sm:pb-40"
          accordionList={accordionNestedContentList}
        />
      ) : (
        accordionNestedContentList?.length > 0 && (
          <TextAccordionNestedList
            containerClass="w-full flex bg-white planet:bg-gray-darker"
            accordionList={accordionNestedContentList}
            extraClass="sm:pt-14 sm:pb-28"
            isOpenTag={isOpenTag}
          />
        )
      )}

      {accordionContentList && (
        <TextAccordionList accordionList={accordionContentList} />
      )}

      {caseStudies && caseStudies?.length > 0 && (
        <TextArticles {...caseStudyConfig} />
      )}

      {(extraContent?.title || extraContent?.description) &&
        !isClimateActionPage &&
        !isBursaries && (
          <TextExtraContent isIllustration={true} content={extraContent} />
        )}

      {featuredBanner?.sourceUrl && <TextWideImage {...featuredBanner} />}

      {brandMessageBanner?.genBrandMessageTitle && (
        <TextWideImage brandMessageBanner={brandMessageBanner} />
      )}

      {extraCTAButton?.btnLabel && extraCTAButton?.btnTarget && (
        <TextExtraCTA {...extraCTAButton} />
      )}

      {isOpenDay &&
        openDayGallery?.videos &&
        openDayGallery?.videos?.length > 0 && (
          <TextPageVideoGallery
            galleryList={openDayGallery}
            mobileScroll={true}
          />
        )}
    </>
  );
};

export default TextPageComponent;
